<script lang="ts">
  // The fitness context around an activity: what shape the athlete was in going into it, and how
  // long each energy system needs to recover from it. Both come from the activity's own history
  // (see activityFitnessSummary() in app/Http/helpers.php), so this is what was true then, not now.
  //
  // Side by side above phone width; on a phone each becomes a labelled row so it lines up with the
  // metric table above it.
  import { cn } from '@/lib/utils';
  import type { ActivityFitnessSummary, CardLayout } from './activityCardUtils';

  export let summary: ActivityFitnessSummary | null | undefined = null;
  export let layout: CardLayout = 'lg';

  $: before = summary?.before ?? null;
  $: recovery = summary?.recovery_days ?? null;
  $: stacked = layout === 'xs';

  // Same wording as the recovery summary on the Today page: anything under two days reads better
  // in hours than as "0.7 days".
  type Recovery = { text: string; recovered: boolean };
  function formatRecovery(days: number | undefined): Recovery | null {
    if (days == null || !isFinite(days)) return null;
    const hours = Math.round(days * 24);
    // Nothing left to wait for. Shown as a tick rather than a word, so the row stays a row of
    // durations instead of mixing prose in among "3d" and "11d" -- the title carries the meaning.
    if (hours < 1) return { text: 'Fully recovered', recovered: true };
    if (days < 2) return { text: `${hours}h`, recovered: false };
    return { text: `${Math.round(days)}d`, recovered: false };
  }

  type RecoveryEntry = { label: string; value: Recovery; color: string };
  let recoveryEntries: RecoveryEntry[] = [];
  $: recoveryEntries = recovery
    ? ([
        { label: 'Low', value: formatRecovery(recovery.low), color: 'var(--color-xlss)' },
        { label: 'High', value: formatRecovery(recovery.high), color: 'var(--color-xhss)' },
        { label: 'Peak', value: formatRecovery(recovery.peak), color: 'var(--color-xpss)' },
      ].filter((entry): entry is RecoveryEntry => entry.value != null))
    : [];

  // ts_rating is the 0-1 fill of the same five-star meter the activity details page draws.
  $: starFill = Math.max(0, Math.min(1, before?.ts_rating ?? 0)) * 100;
</script>

{#if before || recoveryEntries.length}
  <div
    class={cn(
      'text-sm',
      stacked
        ? 'flex flex-col divide-y divide-x-0 divide-solid divide-border'
        : 'flex flex-row flex-wrap items-start gap-x-6 gap-y-2'
    )}
  >
    {#if before}
      <div
        class={cn(
          'min-w-0',
          stacked ? 'flex flex-row items-center justify-between gap-3 py-1.5 first:pt-0 last:pb-0' : ''
        )}
      >
        <div class={cn('uppercase tracking-wider text-muted-foreground leading-none shrink-0', stacked ? 'text-[11px]' : 'text-[10px]')}>
          Going in
        </div>
        <div class={cn('flex flex-row items-center gap-2 min-w-0', stacked ? '' : 'mt-1')}>
          <!-- The font size and line height live on the wrapper so that both layers inherit them.
               Left on the two inner spans instead, the wrapper's own strut is the surrounding 14px
               text: the grey stars sit on that baseline while the coloured overlay starts at the
               top of the box, and the two end up a few pixels apart. -->
          <span class="relative inline-block leading-none whitespace-nowrap text-[11px] tracking-[1px]" title={before.cat}>
            <span class="text-muted-foreground/40">★★★★★</span>
            <span
              class="absolute inset-y-0 left-0 overflow-hidden"
              style="width: {starFill}%; color: {before.icon_color ?? before.color ?? 'currentColor'}"
            >★★★★★</span>
          </span>
          <span class="font-semibold truncate">{before.cat ?? ''}</span>
          {#if before.form_cat}
            <span
              class="rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none text-white whitespace-nowrap"
              style="background-color: {before.icon_color ?? before.color ?? '#707377'}"
            >{before.form_cat}</span>
          {/if}
        </div>
      </div>
    {/if}

    {#if recoveryEntries.length}
      <div
        class={cn(
          'min-w-0',
          stacked ? 'flex flex-row items-center justify-between gap-3 py-1.5 first:pt-0 last:pb-0' : ''
        )}
      >
        <div
          class={cn('uppercase tracking-wider text-muted-foreground leading-none shrink-0', stacked ? 'text-[11px]' : 'text-[10px]')}
          title="Time needed for each energy system to recover after this activity"
        >
          Recovery after
        </div>
        <div class={cn('flex flex-row items-center gap-3', stacked ? '' : 'mt-1')}>
          {#each recoveryEntries as entry}
            <span class="inline-flex items-center gap-1 whitespace-nowrap" title="{entry.label} system: {entry.value.recovered ? entry.value.text : entry.value.text + ' to go'}">
              <span class="inline-block h-1.5 w-1.5 rounded-full" style="background-color: {entry.color}" />
              {#if entry.value.recovered}
                <i class="fa-solid fa-check text-[#5CA907]" aria-label={entry.value.text}></i>
              {:else}
                <span class="font-semibold">{entry.value.text}</span>
              {/if}
            </span>
          {/each}
        </div>
      </div>
    {/if}
  </div>
{/if}
